import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SEO from "@/components/SEO";
import CoverageWaitlistForm from "@/components/CoverageWaitlistForm";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { SEARCH } from "@/constants/testIds";
import { toast } from "sonner";
import {
  Filter, MapPin, Star, BadgeCheck, DollarSign, Search as SearchIcon, Loader2, Heart
} from "lucide-react";

const CERT_LIST = ["RBT", "BCBA", "BCaBA", "CPR", "First Aid", "Special Education Teacher", "Registered Nurse", "OT Assistant"];
const LANGS = ["English", "Spanish", "French", "Mandarin", "ASL"];
const GENDERS = ["", "female", "male", "non-binary"];
const RESPITE_SERVICES = [
  ["traditional_respite", "Traditional respite"],
  ["overnight_support", "Overnight support"],
  ["travel_assistance", "Travel assistance"],
  ["party_buffers", "Party buffers"],
  ["community_support", "Community support"],
];

function ChipToggle({ active, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200 ${
        active ? "bg-sage-600 text-white border-sage-600" : "bg-white border-stone2-200 text-sage-800 hover:border-sage-400"
      }`}>
      {children}
    </button>
  );
}

export default function SearchSitters() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialRespite = (searchParams.get("respite_services") || "")
    .split(",").map((s) => s.trim()).filter(Boolean);
  const [filters, setFilters] = useState({
    zip_code: "", max_distance_miles: null,
    min_hourly_rate: null, max_hourly_rate: null,
    certifications: [], min_experience_years: null,
    languages: [], gender: "",
    aba_trained: false, special_ed: false, cpr_first_aid: false, non_smoker: false,
    can_transport: false, comfortable_aggressive: false, comfortable_toileting: false,
    overnight_care: false, travel_support: false,
    allergies_ok: [], verified_only: false,
    respite_services: initialRespite,
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favSet, setFavSet] = useState(new Set());

  const loadFavorites = async () => {
    try {
      const { data } = await api.get("/favorites");
      setFavSet(new Set(data.results.map((f) => f.sitter_id)));
    } catch (e) { console.warn("[SearchSitters] favorites fetch failed", e); }
  };

  const toggleFav = async (sitterId) => {
    try {
      const { data } = await api.post("/favorites", { sitter_id: sitterId });
      if (data.status === "added") setFavSet((s) => new Set(s).add(sitterId));
      else setFavSet((s) => { const n = new Set(s); n.delete(sitterId); return n; });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail?.code === "upgrade_required") {
        toast.error(detail.message || "Upgrade required");
      } else {
        toast.error(extractError(e));
      }
    }
  };

  const load = async (payload = filters) => {
    setLoading(true);
    try {
      const clean = { ...payload };
      Object.keys(clean).forEach((k) => {
        if (clean[k] === "" || clean[k] === null) delete clean[k];
        if (Array.isArray(clean[k]) && clean[k].length === 0) delete clean[k];
        if (typeof clean[k] === "boolean" && !clean[k]) delete clean[k];
      });
      const { data } = await api.post("/sitters/search", clean);
      setResults(data.results);
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); if (user) loadFavorites(); /* initial */ /* eslint-disable-next-line */ }, []);

  const set = (k, v) => setFilters((s) => ({ ...s, [k]: v }));
  const toggleArr = (k, v) => setFilters((s) => {
    const arr = s[k] || [];
    return { ...s, [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] };
  });

  const activeCat = filters.respite_services[0];
  const seoTitleMap = {
    traditional_respite: "Traditional respite sitters — trained for autism | AutismBabysitters",
    overnight_support: "Overnight autism support sitters — trained for bedtime & night care | AutismBabysitters",
    travel_assistance: "Travel assistance sitters — airport & road trip support for autism | AutismBabysitters",
    party_buffers: "Party buffer sitters — 1:1 event support for autism | AutismBabysitters",
    community_support: "Community support sitters — outings & errands for autism families | AutismBabysitters",
  };
  const seoDescMap = {
    traditional_respite: "Book vetted, ABA-trained sitters for short-term in-home autism respite. Sensory tools, routines, and calming strategies honored from minute one.",
    overnight_support: "Find sitters trained for autism bedtime routines, night-waking, and safe overnight supervision. RBTs, BCBAs and CPR-certified caregivers.",
    travel_assistance: "Autism travel support — sitters who help at airports, on road trips, and at appointments. Ease transitions and wait-time stress.",
    party_buffers: "Bring a calm, familiar 1:1 sitter to birthdays, weddings, and events. Keep your child regulated in high-stimulation settings.",
    community_support: "Community outings, grocery runs, and library visits with an autism-trained sitter who knows your child's triggers and cues.",
  };
  const seoTitle = seoTitleMap[activeCat] || "Find specialized autism sitters near you | AutismBabysitters";
  const seoDesc = seoDescMap[activeCat] || "Search RBT, BCBA, and CPR-certified autism babysitters. Filter by distance, certifications, ABA training, overnight care, travel support, and more.";
  const seoCanonical = activeCat
    ? `https://autismbabysitters.com/search?respite_services=${activeCat}`
    : "https://autismbabysitters.com/search";
  const searchJsonLd = activeCat ? {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": (seoTitleMap[activeCat] || "").split(" — ")[0],
    "provider": {
      "@type": "Organization",
      "name": "AutismBabysitters",
      "url": "https://autismbabysitters.com/",
    },
    "areaServed": "United States",
    "description": seoDescMap[activeCat],
    "url": seoCanonical,
  } : null;

  return (
    <div className="min-h-screen bg-stone2-50" data-testid={SEARCH.page}>
      <SEO title={seoTitle} description={seoDesc} canonical={seoCanonical} jsonLd={searchJsonLd} />
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 grid lg:grid-cols-12 gap-6">
        {/* Filters */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="paper-card p-5 sticky top-20" data-testid={SEARCH.filters}>
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-4 h-4 text-sage-600" />
              <h2 className="font-heading font-semibold text-sage-900">Filters</h2>
            </div>

            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="field-label">ZIP</label>
                  <input value={filters.zip_code} onChange={(e) => set("zip_code", e.target.value)}
                    data-testid="filter-zip" placeholder="e.g. 90001"
                    className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="field-label">Distance (mi)</label>
                  <input type="number" min="1" value={filters.max_distance_miles || ""}
                    onChange={(e) => set("max_distance_miles", e.target.value ? Number(e.target.value) : null)}
                    data-testid="filter-distance"
                    className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="field-label">Min $/hr</label>
                  <input type="number" min="0" value={filters.min_hourly_rate || ""}
                    onChange={(e) => set("min_hourly_rate", e.target.value ? Number(e.target.value) : null)}
                    data-testid="filter-min-rate"
                    className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="field-label">Max $/hr</label>
                  <input type="number" min="0" value={filters.max_hourly_rate || ""}
                    onChange={(e) => set("max_hourly_rate", e.target.value ? Number(e.target.value) : null)}
                    data-testid="filter-max-rate"
                    className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
                </div>
              </div>

              <div>
                <label className="field-label">Min years experience</label>
                <input type="number" min="0" value={filters.min_experience_years || ""}
                  onChange={(e) => set("min_experience_years", e.target.value ? Number(e.target.value) : null)}
                  data-testid="filter-min-exp"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
              </div>

              <div>
                <label className="field-label">Certifications</label>
                <div className="flex flex-wrap gap-1.5">
                  {CERT_LIST.map((c) => (
                    <ChipToggle key={c} active={filters.certifications.includes(c)}
                      onClick={() => toggleArr("certifications", c)}
                      testId={`filter-cert-${c}`}>{c}</ChipToggle>
                  ))}
                </div>
              </div>

              <div>
                <label className="field-label">Respite services</label>
                <div className="flex flex-wrap gap-1.5">
                  {RESPITE_SERVICES.map(([k, l]) => (
                    <ChipToggle key={k} active={filters.respite_services.includes(k)}
                      onClick={() => toggleArr("respite_services", k)}
                      testId={`filter-respite-${k}`}>{l}</ChipToggle>
                  ))}
                </div>
              </div>

              <div>
                <label className="field-label">Languages</label>
                <div className="flex flex-wrap gap-1.5">
                  {LANGS.map((l) => (
                    <ChipToggle key={l} active={filters.languages.includes(l)}
                      onClick={() => toggleArr("languages", l)}
                      testId={`filter-lang-${l}`}>{l}</ChipToggle>
                  ))}
                </div>
              </div>

              <div>
                <label className="field-label">Gender</label>
                <select value={filters.gender} onChange={(e) => set("gender", e.target.value)}
                  data-testid="filter-gender"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm">
                  {GENDERS.map((g) => <option key={g} value={g}>{g || "any"}</option>)}
                </select>
              </div>

              <div className="space-y-2">
                {[
                  ["aba_trained", "ABA trained"],
                  ["special_ed", "Special education"],
                  ["cpr_first_aid", "CPR / First Aid"],
                  ["non_smoker", "Non-smoker"],
                  ["can_transport", "Can transport"],
                  ["comfortable_aggressive", "Comfortable w/ aggressive"],
                  ["comfortable_toileting", "Comfortable w/ toileting"],
                  ["overnight_care", "Overnight care"],
                  ["travel_support", "Travel support"],
                  ["verified_only", "Verified sitters only"],
                ].map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2 text-sage-900 text-sm">
                    <input type="checkbox" checked={!!filters[k]} onChange={(e) => set(k, e.target.checked)}
                      data-testid={`filter-${k}`}
                      className="rounded text-sage-600 focus:ring-sage-500" />
                    {l}
                  </label>
                ))}
              </div>
            </div>

            <button type="button" onClick={() => load()} data-testid={SEARCH.submit}
              className="btn-primary w-full mt-5 justify-center">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <SearchIcon className="w-4 h-4" />} Search
            </button>
          </div>
        </aside>

        {/* Results */}
        <section className="lg:col-span-8 xl:col-span-9">
          <div className="flex items-end justify-between">
            <div>
              <span className="section-eyebrow">Sitters near you</span>
              <h1 className="mt-2 text-2xl lg:text-3xl font-heading font-bold text-sage-900">
                {results.length} specialized {results.length === 1 ? "sitter" : "sitters"}
              </h1>
            </div>
          </div>

          {loading && (
            <div className="mt-6 flex items-center gap-2 text-stone2-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Searching…
            </div>
          )}

          {!loading && results.length === 0 && (
            <div className="mt-6 space-y-4" data-testid="search-empty-state">
              <div className="paper-card p-8 text-center">
                <p className="text-stone2-600 font-heading font-semibold text-lg">
                  No verified sitters match your filters yet.
                </p>
                <p className="mt-1 text-sm text-stone2-500">
                  Try widening your ZIP or removing a filter — or join our coverage waitlist below.
                </p>
              </div>
              <div className="max-w-lg mx-auto">
                <CoverageWaitlistForm source="search" />
              </div>
            </div>
          )}

          <div className="mt-6 grid md:grid-cols-2 xl:grid-cols-2 gap-4">
            {results.map((s) => (
              <article key={s.id} className="paper-card p-5 hover:-translate-y-1 transition-transform duration-200" data-testid={SEARCH.result(s.id)}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-full bg-sage-600 text-white flex items-center justify-center font-heading font-bold text-lg">
                      {s.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <div className="font-heading font-semibold text-sage-900 flex items-center gap-1">
                        {s.name}
                        {s.verified && <BadgeCheck className="w-4 h-4 text-sage-600" />}
                      </div>
                      <div className="text-xs text-stone2-500 flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.city || "—"} {s.state || ""}</span>
                        {typeof s.distance_miles === "number" && <span>· {s.distance_miles} mi</span>}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-heading font-bold text-sage-900"><DollarSign className="w-3.5 h-3.5 inline" />{s.hourly_rate}<span className="text-xs text-stone2-500">/hr</span></div>
                    <div className="text-xs text-stone2-500 flex items-center gap-1 justify-end">
                      <Star className="w-3 h-3 text-ochre-500 fill-ochre-500" /> {s.rating_avg || "—"} ({s.rating_count || 0})
                    </div>
                  </div>
                </div>

                <p className="mt-3 text-sm text-stone2-600 line-clamp-2">{s.biography || "—"}</p>

                <div className="mt-3 flex flex-wrap gap-1">
                  {(s.certifications || []).slice(0, 4).map((c) => (
                    <span key={c} className="chip">{c}</span>
                  ))}
                  {s.aba_trained && <span className="chip-ochre">ABA</span>}
                  {s.overnight_care && <span className="chip-ochre">Overnight</span>}
                </div>

                {(s.respite_services || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1" data-testid={`sitter-respite-list-${s.id}`}>
                    {(s.respite_services || []).map((svc) => {
                      const label = (RESPITE_SERVICES.find(([k]) => k === svc) || [svc, svc])[1];
                      return <span key={svc} className="chip">{label}</span>;
                    })}
                  </div>
                )}

                <div className="mt-4 flex gap-2 items-center">
                  <Link to={`/sitters/${s.id}`} data-testid={SEARCH.viewSitter(s.id)} className="btn-primary text-sm">View profile</Link>
                  <button type="button" onClick={() => toggleFav(s.id)}
                    data-testid={`fav-toggle-${s.id}`}
                    title={favSet.has(s.id) ? "Remove favorite" : "Save favorite"}
                    className={`p-2 rounded-full transition-colors duration-200 ${
                      favSet.has(s.id)
                        ? "bg-danger/10 text-danger hover:bg-danger/20"
                        : "bg-stone2-100 text-stone2-600 hover:bg-stone2-200"
                    }`}>
                    <Heart className={`w-4 h-4 ${favSet.has(s.id) ? "fill-danger" : ""}`} />
                  </button>
                  {s.plan === "sitter_enhanced" && (
                    <span className="chip-ochre text-[10px]" data-testid={`sitter-plan-enhanced-${s.id}`}>Enhanced</span>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
